<template>
  <div>
    <el-row class="title-bar vertical-center-box" type="flex" justify="space-between">
      <div class="title-h4">저장소 목록</div>
      <div>
        <w-header-search-button @click="onRealIndexSearchToolVisible"></w-header-search-button>
      </div>
    </el-row>
    <div>
      <el-table-wrapper v-loading="loading" :column-default="columnDefault" :data="realIndexDatas" :columns="indexStoreColumns" :pagination="true" highlight-current-row
        :show-search-tool="realIndexSearchToolVisible" :show-custom-header="true" ref="logAlarmTable" header-align="center">
        <template slot-scope="scope" slot="snapshot-slot">
          <el-row type="flex" justify="center">
            <el-button v-if="processingBackupIndex !== scope.$index" size="mini" @click="createSnapshot(scope.row, scope.$index)">백업</el-button>
            <i v-if="processingBackupIndex === scope.$index" class="el-icon-loading" />
            <!-- <el-button size="mini">복구</el-button> -->
          </el-row>
        </template>
        <template slot-scope="scope" slot="index-health-slot">
          <w-eventlevel-cell type="circle" :eventlevel-id="indexHealthColor[scope.row.health]">
            <span slot="content"></span>
          </w-eventlevel-cell>
        </template>
        <template slot-scope="scope" slot="store-size-slot">
          {{ scope.row.storeSize.toUpperCase() }}
        </template>
        <template slot-scope="scope" slot="docs-count-slot">
          {{ convertThousandSeparatorFormat(Number(scope.row.docsCount)) }}
        </template>
      </el-table-wrapper>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { forEach, isEmpty, find, isNil } from 'lodash';

import LogStoreAPI from '@/api/log/log-store';
import { columnDefault } from '@/constants/table';
import { getDateToString } from '@/utils/date';
import { getValueAfterCheckEmpty } from '@/utils/string';
import { convertThousandSeparatorFormat } from '@/utils/number';
import msgUtils from '@/utils/message';
import WEventlevelCell from '@/components/table/EventlevelCell';

const snapshotStatusCheckTask = 'snapshotStatusCheckTask';
const onlyBackupLastDays = '지난 저장소만 백업할 수 있습니다.';
const successBackup = '저장소를 백업하였습니다.';
const failBackup = '저장소를 백업하지 못하였습니다.';
const alreadyProcessingBackup = '이미 백업 또는 복구를 진행 중인 저장소가 있습니다. 작업이 완료될 때까지 다른 저장소의 백업 및 복구가 불가능합니다.';

export default {
  name: 'WIndexTable',
  components: {
    WEventlevelCell,
  },
  props: {
    selectedDataindex: Object,
    isProgressedRestore: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      indexColumns: [
        {
          label: '저장소 이름',
          prop: 'dataindexName',
          searchable: true,
          sortable: true,
        },
      ],
      usingDataindexes: [],
      columnDefault,
      realIndexDatas: [],
      indexStoreColumns: [
        {
          label: 'Health',
          prop: 'health',
          width: 60,
          align: 'center',
          scopedSlot: 'index-health-slot',
        },
        {
          label: '상태',
          prop: 'status',
          width: '100',
          searchable: true,
          sortable: true,
        },
        {
          label: '저장소명',
          prop: 'index',
          searchable: true,
          sortable: true,
        },
        {
          label: '레코드 수',
          prop: 'docsCount',
          width: '100',
          scopedSlot: 'docs-count-slot',
          searchable: true,
          sortable: true,
        },
        {
          label: '용량',
          prop: 'byteSize',
          width: '100',
          scopedSlot: 'store-size-slot',
          searchable: true,
          searchMethod: this.searchStoreSize,
          sortable: true,
        },
        {
          label: '생성일',
          prop: 'createdDay',
          width: '160',
          searchable: true,
          searchType: 'date',
          sortable: true,
        },
        {
          label: '보관 기한',
          prop: 'retentionDate',
          width: '160',
          searchType: 'date',
          searchable: true,
          sortable: true,
        },
        {
          label: '백업',
          width: 60,
          scopedSlot: 'snapshot-slot',
        },
      ],
      indexHealthColor: {},
      isProgressedSnapshot: false,
      dataindexSearchToolVisible: false,
      realIndexSearchToolVisible: false,
      processingBackupIndex: null,
    };
  },
  watch: {
    isProgressedSnapshot(value) {
      this.$emit('change-snapshot-state', value);
    },
    selectedDataindex(value) {
      if (!isEmpty(value) && !isNil(value.dataindexOrigin)) {
        this.loadIndices(value.dataindexOrigin);
      }
    },
  },
  computed: mapGetters(['eventlevelInfoWithNM']),
  mounted() {
    LogStoreAPI.getDataindexes().then((response) => {
      if (response.data.success) {
        this.usingDataindexes = response.data.data;
      }
    }).catch((e) => {
      this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '저장소', false));
      throw new Error(e);
    });
    this.$nextTick(() => {
      this.indexHealthColor = {
        green: this.eventlevelInfoWithNM.MN.eventlevelId,
        yellow: this.eventlevelInfoWithNM.MJ.eventlevelId,
        red: this.eventlevelInfoWithNM.DN.eventlevelId,
      };
    });
  },
  beforeDestroy() {
    this.$taskManager.removeTask(snapshotStatusCheckTask);
  },
  methods: {
    convertThousandSeparatorFormat,
    /**
     * 선택된 저장소 설정 하위의 저장소 목록을 ES에서 조회해온다.
     * @author Minyeong
     * @param  {[type]} dataindexOrigin [description]
     * @return {[type]}                 [description]
     */
    loadIndices(dataindexOrigin) {
      const params = {
        params: {
          dataindexOrigin,
        },
      };
      this.loading = true;
      LogStoreAPI.getLogIndices(params).then((response) => {
        if (response.data.success) {
          this.realIndexDatas = [];
          const allIndices = response.data.data;
          forEach(allIndices, (item) => {
            const indexNameArr = item.index.split('-');
            const datetime = indexNameArr[indexNameArr.length - 1];
            if (datetime.split('.').length === 4) {
              // creationInterval이 H일 경우 : 생성날짜가 YYYY.MM.DD.HH 형식으로 들어옴 > Date형태로 가공
              const temp = datetime;
              const lastIndex = temp.lastIndexOf('.');
              const createdString = `${temp.substring(0, lastIndex)} ${temp.substring(lastIndex + 1, temp.length)}:00:00`;
              const dataindex = find(this.usingDataindexes, (dix) => dix.dataindexOrigin === `${indexNameArr[0]}-${indexNameArr[1]}`);
              if (!isEmpty(dataindex)) {
                item.createdDay = getDateToString(new Date(createdString), 'YYYY-MM-DD HH:mm:ss');
                item.retentionDate = this.getRetentionDate(item.createdDay, dataindex.retentionAge);
                this.realIndexDatas.push(item);
              }
            } else if (datetime.split('.').length === 3) {
              // creationInterval이 D일 경우
              item.createdDay = getDateToString(datetime, 'YYYY-MM-DD HH:mm:ss');
              const dataindex = find(this.usingDataindexes, (dix) => dix.dataindexOrigin === `${indexNameArr[0]}-${indexNameArr[1]}`);
              if (!isEmpty(dataindex)) {
                item.retentionDate = this.getRetentionDate(item.createdDay, dataindex.retentionAge);
                this.realIndexDatas.push(item);
              }
            }
          });
        } else {
          this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '저장소', false));
        }
      }).catch((e) => {
        this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '저장소', false));
        throw new Error(e);
      }).finally(() => {
        this.loading = false;
      });
    },
    createSnapshot(row, index) {
      if (this.isProgressedSnapshot || this.isProgressedRestore || !isNil(this.processingBackupIndex)) {
        this.$message(msgUtils.getCustomMsgData(alreadyProcessingBackup));
        return;
      }
      const today = new Date();
      const createdDay = new Date(row.createdDay);
      const indexNameArr = row.index.split('-');
      const dataindex = find(this.usingDataindexes, (dix) => dix.dataindexOrigin === `${indexNameArr[0]}-${indexNameArr[1]}`);
      if (!isEmpty(dataindex)) {
        if (dataindex.creationInterval === 'D' && today.getFullYear() === createdDay.getFullYear() && today.getMonth() === createdDay.getMonth()
          && today.getDate() <= createdDay.getDate()) {
          this.$message(msgUtils.getCustomMsgData(onlyBackupLastDays));
          return;
        } else if (dataindex.creationInterval === 'H' && today.getFullYear() === createdDay.getFullYear() && today.getMonth() === createdDay.getMonth()
          && today.getDate() <= createdDay.getDate() && today.getHours() <= createdDay.getHours()) {
          this.$message(msgUtils.getCustomMsgData(onlyBackupLastDays));
          return;
        }
        this.$confirm(...msgUtils.getCustomConfirmData('백업한 저장소의 데이터는 조회할 수 없습니다. 백업하시겠습니까?')).then(() => {
          this.processingBackupIndex = index;
          this.$message(msgUtils.getCustomMsgData('백업을 진행합니다. 작업이 완료될 때까지 다른 저장소의 백업 및 복구가 불가능합니다.'));
          const params = {
            indexName: row.index,
          };
          const snapshotParams = {
            snapshotName: row.index,
          };
          this.isProgressedSnapshot = true;
          LogStoreAPI.createSnapshot(params).then((snapshotRes) => {
            if (snapshotRes.data.success && snapshotRes.data.data === 'SUCCESS') {
              this.deleteIndex(params).then((delIndexRes) => {
                if (delIndexRes.data.success && delIndexRes.data.data) {
                  // 스냅샷 저장 성공 후 인덱스 삭제 성공 (정상적으로 백업 완료)
                  this.$emit('update-snapshot');
                  this.$message(msgUtils.getCustomResultMsgData(successBackup, true));
                  this.processingBackupIndex = null;
                  this.isProgressedSnapshot = false;
                  this.loadIndices(this.selectedDataindex.dataindexOrigin);
                } else {
                  // 스냅샷 저장은 했으나 인덱스 삭제 실패함 > running 중인 스냅샷을 중단하고 삭제함
                  this.deleteSnapshot(snapshotParams).then((delSnapshotRes) => {
                    if (delSnapshotRes.data.success && delSnapshotRes.data.data) {
                      this.$message(msgUtils.getCustomResultMsgData(failBackup, false));
                      this.processingBackupIndex = null;
                      this.isProgressedSnapshot = false;
                    }
                  });
                }
              });
            } else if (snapshotRes.data.success && snapshotRes.data.data === 'IN_PROGRESS') {
              this.$message(msgUtils.getCustomMsgData(alreadyProcessingBackup));
            } else {
              // 저장소 백업에 문제 발생 > 현재 running snapshot의 status를 확인한 후 다른 작업 진행
              this.createSnapshotStatusCheckTask(snapshotParams, params, true);
            }
          }).catch((e) => {
            this.$message(msgUtils.getResultMsgData(msgUtils.action.SAVE, '백업', false));
            throw new Error(e);
          });
        }).catch(() => {});
      }
    },
    /**
     * 백업 상태를 주기적으로(3초) 확인하는 태스크를 생성한다.
     * @author Minyeong
     * @param  {[Object]} snapshotParams [스냅샷 parameter]
     * @param  {[Object]} params         [인덱스 parameter]
     * @param  {[Boolean]} andStart       [태스크 생성 후 시작 여부]
     * @return {[type]}                [description]
     */
    createSnapshotStatusCheckTask(snapshotParams, params, andStart) {
      this.$taskManager.createTask(snapshotStatusCheckTask, 3, () => {
        this.checkSnapshotStatus(snapshotParams).then((statusRes) => {
          if (statusRes.data.success && statusRes.data.data === 'SUCCESS') {
            // 스냅샷의 status가 SUCCESS일 때 : 무사히 백업이 완료되었을 때
            // 주기적으로 상태 확인하던 interval 함수를 제거하고 백업이 완료된 스냅샷의 인덱스를 삭제함
            this.$taskManager.removeTask(snapshotStatusCheckTask);
            this.deleteIndex(params).then((delIndexRes) => {
              if (delIndexRes.data.success && delIndexRes.data.data) {
                // 인덱스까지 삭제가 완료되었을 때 > 스냅샷 정상적으로 백업 완료
                this.$emit('update-snapshot');
                this.$message(msgUtils.getCustomResultMsgData(successBackup, true));
                this.processingBackupIndex = null;
                this.isProgressedSnapshot = false;
                this.loadIndices(this.selectedDataindex.dataindexOrigin);
              } else {
                // 인덱스 삭제에 실패했을 경우 > 생성이 완료된 스냅샷을 삭제함 (스냅샷 백업 실패)
                this.deleteSnapshot(snapshotParams).then((delSnapshotRes) => {
                  this.$message(msgUtils.getCustomResultMsgData(failBackup, false));
                  if (delSnapshotRes.data.success && delSnapshotRes.data.data) {
                    this.processingBackupIndex = null;
                    this.isProgressedSnapshot = false;
                  }
                });
              }
            });
          } else if (!statusRes.data.success || (statusRes.data.data !== 'IN_PROGRESS' && statusRes.data.data !== 'SUCCESS' && statusRes.data.data !== 'STARTED')) {
            // 스냅샷 생성에 실패했을 때 > 상태 확인 interval 함수 중단 후 해당 스냅샷 삭제
            this.$taskManager.removeTask(snapshotStatusCheckTask);
            this.$message(msgUtils.getCustomResultMsgData(failBackup, false));
            this.processingBackupIndex = null;
            this.isProgressedSnapshot = false;
            this.deleteSnapshot(snapshotParams);
          }
        });
      });
      if (andStart) {
        this.$taskManager.startTask(snapshotStatusCheckTask);
      }
    },
    getDateToString,
    getValueAfterCheckEmpty,
    /**
     * 보관 기한을 계산한다.
     * @author Minyeong
     * @param  {[type]} datetime [description]
     * @return {[type]}          [description]
     */
    getRetentionDate(datetime) {
      const date = new Date(datetime);
      date.setDate(date.getDate() + this.selectedDataindex.retentionAge);

      return getDateToString(date.getTime(), 'YYYY-MM-DD HH:mm:ss');
    },
    onRealIndexSearchToolVisible() {
      this.realIndexSearchToolVisible = !this.realIndexSearchToolVisible;
    },
    /**
     * 파일 크기 검색
     * @author Minyeong
     * @param  {[type]} value [description]
     * @param  {[type]} row   [description]
     * @return {[type]}       [description]
     */
    searchStoreSize(value, row) {
      return (row.storeSize.toUpperCase()).indexOf(value.toUpperCase()) > -1;
    },
    checkSnapshotStatus(params) {
      return LogStoreAPI.checkSnapshotStatus(params);
    },
    deleteIndex(params) {
      return LogStoreAPI.deleteIndex(params);
    },
    deleteSnapshot(params) {
      return LogStoreAPI.deleteSnapshot(params);
    },
  },
};
</script>

<style>

</style>



// WEBPACK FOOTER //
// src/views/log-analysis/popup/store/IndexTable.vue